import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getAllPhoneNumber, getPhoneNumberByOrgId } from '../../utils/api/phoneNumberAPI';
import { getOutbondCall } from '../../utils/api/callAPI';
import { toast } from 'sonner';

export default function PlaceOutbondCall({ onClose, activeAssistantId }) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [numbers, setNumbers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [variableFields, setVariableFields] = useState({});
  const [variables, setVariables] = useState([]);

  const assistantDetails = useSelector((state) => state.assistants.activeDetail.details);
  const firstMessage = assistantDetails?.firstMessage || '';
  const promptMessage = assistantDetails?.model?.messages?.[0]?.content || '';
  const orgId = useSelector((state) => state?.orgData?.orgData?.organization.id)
  console.log(orgId, "orgId in PlaceOutbondCall");

  // Extract variables like {{user_name}} from a string
  const extractVariables = (text) => {
    const matches = text.match(/{{\s*([\w\d_]+)\s*}}/g) || [];
    return matches.map((match) => match.replace(/{{\s*|\s*}}/g, ''));
  };

  useEffect(() => {
    if (orgId) {
      getPhoneNumberByOrgId(orgId)
        .then((data) => {
          const sortedNumbers = data.phoneNumbers.sort((a, b) =>
            a.did.localeCompare(b.did)
          );
          setNumbers(sortedNumbers);
          if (sortedNumbers.length > 0) {
            setPhoneNumberId(sortedNumbers[0].id);
          }
        })
        .catch((e) => {
          console.error('Failed to fetch phone numbers:', e);
          toast.error('Failed to fetch phone numbers');
        });
    }
  }, [orgId]);

  useEffect(() => {
    const varsInFirst = extractVariables(firstMessage);
    const varsInPrompt = extractVariables(promptMessage);
    const uniqueVars = [...new Set([...varsInFirst, ...varsInPrompt])];
    setVariables(uniqueVars);

    // Initialize variable fields
    setVariableFields((prev) => {
      const updated = { ...prev };
      uniqueVars.forEach((key) => {
        if (!(key in updated)) {
          updated[key] = '';
        }
      });
      return updated;
    });
  }, [firstMessage, promptMessage]);

  const handlePlaceCall = async () => {
    if (!phoneNumber || !phoneNumberId) {
      toast.error('Please enter a valid phone number and select a calling number.');
      return;
    }

    if (!/^\d{10}$/.test(phoneNumber)) {
      toast.error('Please enter a valid 10-digit phone number.');
      return;
    }

    for (const key of variables) {
      if (!variableFields[key]?.trim()) {
        toast.error(`Please fill the "${key}" field.`);
        return;
      }
    }

    const payload = {
      assistantId: activeAssistantId,
      assistantOverrides: {
        variableValues: variableFields,
      },
      phoneNumberId,
      customer: {
        number: `+91${phoneNumber}`,
      },
    };

    try {
      setIsLoading(true);
      const response = await getOutbondCall(payload);
      toast.success(response?.messages || 'Call placed successfully');
      onClose();
    } catch (err) {
      console.error('Call failed:', err);
      toast.error(err?.message || 'Failed to place call. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (numbers.length === 0) {
    return (
      <div className="modal-view-container">
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '450px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            margin: 'auto',
          }}
        >
          <p style={{ textAlign: 'center' }}>Loading available numbers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-view-container">
      <div
        className="p-20"
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '450px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div style={{ fontSize: '18px', fontWeight: '600', color: 'black' }}>
            Place outbound call
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#666',
              cursor: 'pointer',
            }}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Phone Input */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
            Enter phone number
            <div style={{ fontSize: '12px', color: '#666' }}>
              (for example: 81028565**)
            </div>
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
            <div
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                backgroundColor: '#f3f3f3',
              }}
            >
              +91
            </div>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Phone number"
              maxLength={10}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '6px',
              }}
            />
          </div>
        </div>

        {/* Call method selector */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: '14px', marginBottom: '4px', display: 'block' }}>
            Calls can be made using:
          </label>
          <select
            value={phoneNumberId}
            onChange={(e) => setPhoneNumberId(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '6px',
            }}
          >
            {numbers.map((val) => (
              <option key={val.id} value={val.id}>
                {val.did}
              </option>
            ))}
          </select>
        </div>

        {/* Dynamic Variable Fields */}
        {variables.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '14px', marginBottom: '8px', display: 'block' }}>
              Fill in template variables:
            </label>
            {variables.map((key) => (
              <div key={`var-${key}`} style={{ marginBottom: '10px' }}>
                <input
                  type="text"
                  placeholder={`Enter ${key}`}
                  value={variableFields[key]}
                  onChange={(e) =>
                    setVariableFields({ ...variableFields, [key]: e.target.value })
                  }
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Footer note */}
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
          You can{' '}
          <Link to="#" style={{ color: '#2563eb', textDecoration: 'underline' }}>
            request phone numbers
          </Link>{' '}
          to start making calls from your own custom numbers.
        </p>

        {/* Action button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handlePlaceCall}
            disabled={isLoading}
            className="btn btn-primary"
          >
            {isLoading ? (
              <>
                <span className="spinner" />
                Calling...
              </>
            ) : (
              'Place call'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
